import { Component, Input, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import {
  numberValuesChartOptions,
  pctValuesChartOptions,
  reportChartBarOptions,
} from '../highcharts-options';
import { QuickBuildService, UIFilter } from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { BaseReportComponent, ChartContextMenuItem } from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { ToggleOption } from '../../components/option-toggle/option-toggle.component';
import { DemographicsCurrentConfig } from '../interfaces/report-config.interfaces';
import { SearchFilter } from 'ldt-people-api';
import * as dayjs from 'dayjs';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const FIELDS_TO_GROUP_BY = ['jobs.function', 'jobs.level'];
const REPORT_NAME = 'demographics';

@Component({
    selector: 'app-reports-demographics-current',
    templateUrl: './demographics-current.component.html',
    styleUrls: ['./demographics-current.component.scss'],
    standalone: false
})
export class DemographicsCurrentComponent extends BaseReportComponent<DemographicsCurrentConfig> {
  @Input() field: string;
  @Input() allowFieldSelection = true;
  @Input() boxShadow: boolean = true;
  @Input() maxSeries: number = 20;

  // We want to accept an array of fields here, but we need to change them to ToggleOptions
  @Input()
  set breakdownByOptions(fields: string[]) {
    this._breakdownByOptions = fields || FIELDS_TO_GROUP_BY;
    this.breakdownByToggleOptions = this._breakdownByOptions.map((field) => ({
      value: field,
      displayText: this.getFieldDisplayName(field),
    }));
  }
  get breakdownByOptions(): string[] {
    return this._breakdownByOptions;
  }
  private _breakdownByOptions: string[] = FIELDS_TO_GROUP_BY;

  breakdownByToggleOptions: ToggleOption[] = [];

  @Input()
  set selectedBreakdownBy(value: string | undefined) {
    if (value) {
      this._selectedBreakdownBy = value;
    }
  }
  get selectedBreakdownBy(): string {
    return this._selectedBreakdownBy;
  }

  @Input()
  set selectedValueType(value: string | undefined) {
    if (value) {
      this._selectedValueType = value;
    }
  }
  get selectedValueType(): string {
    return this._selectedValueType;
  }

  private _selectedBreakdownBy: string = FIELDS_TO_GROUP_BY[0];

  valueTypeOptions: ToggleOption[] = [
    { value: 'percentage', displayText: '%' },
    { value: 'number', displayText: '#' },
  ];
  private _selectedValueType: string = 'percentage';

  protected readonly CHART_ID = 'demographics-current';
  protected readonly CHART_DATA_DESC = 'Demographic data for active employees';

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );
    // Initialize toggle options
    this.breakdownByToggleOptions = this._breakdownByOptions.map((field) => ({
      value: field,
      displayText: this.getFieldDisplayName(field),
    }));

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedBreakdownBy', () => this._selectedBreakdownBy);
    this.registerConfigProperty('selectedValueType', () => this._selectedValueType);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportChartBarOptions)
    );
    this.chart.update(pctValuesChartOptions, true, true);
    this.chart.update(
      {
        title: {
          text: 'Active Employees by ' + this.getFieldDisplayName(this._selectedBreakdownBy),
        },
        yAxis: { title: { text: '% of employees' } },
      },
      true,
      true
    );
    this.initializeContextMenu();
  }

  protected shouldRefreshOnChanges(changes: SimpleChanges): boolean {
    if ('field' in changes) {
      this._selectedBreakdownBy = this.field;
      return true;
    }
    return super.shouldRefreshOnChanges(changes);
  }

  protected getChartData(): void {
    if (!this.quickBuild) return;

    this.showLoading();

    const reportParams = {
      group_by: [this._selectedBreakdownBy],
    };
    const sub = this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        REPORT_NAME,
        reportParams,
        this.reportTarget,
        this.quickBuild
      )
      .subscribe({
        next: (data) => {
          Log.d('demographicscurrent data', data);
          const [series, categories] = this.generateChartSeries(
            data.count,
            data.data.demographics,
            this._selectedBreakdownBy
          );
          this.updateChart(
            series,
            categories,
            'Active Employees by ' + this.getFieldDisplayName(this._selectedBreakdownBy)
          );
          this.chartLoadingComplete();
        },
        error: (error) => {
          Log.e('DemographicsCurrentComponent getChartData error', error);
          this.showChartError(error);
        },
      });

    this.subscriptions.add(sub);
  }

  protected generateChartSeries(total: number, data: any, key: string): [any[], any[]] {
    const usePct = this._selectedValueType === 'percentage';

    // Filter out the current company
    const filteredData = data.filter((c: any) => {
      if (
        key === 'jobs.metadata.previous_job.company.name' &&
        c.group_values[0]?.value === this.quickBuild?.name
      ) {
        return false;
      }
      return true;
    });

    const seriesData = filteredData.slice(0, this.maxSeries).map((c: any) => {
      return usePct ? 100 * (c.count_employees / (total || 1)) : c.count_employees;
    });

    const categories = filteredData.slice(0, this.maxSeries).map((c: any) => {
      return c.group_values[0]?.value;
    });

    return [
      [
        {
          id: key,
          name: key,
          showInLegend: false,
          data: seriesData,
          type: 'bar',
        },
      ],
      categories,
    ];
  }

  async resetChartData() {
    switch (this._selectedValueType) {
      case 'percentage':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Pct of Selected Filters' },
          },
        });
        break;
      case 'number':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Number of Employees' },
          },
        });
        break;
    }

    await super.resetChartData();
  }

  onBreakdownByChanged(value: string) {
    if (!value) return;

    this._selectedBreakdownBy = value;
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const series = point.series;
    let category = point.category as string;
    if (!this.quickBuild) {
      Log.s('DemographicsCurrentComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
      });
      return [];
    }

    // Fix for weird previous company situation
    let breakdownBy = this._selectedBreakdownBy;
    if (breakdownBy === 'jobs.metadata.previous_job.company.name') {
      breakdownBy = 'jobs.previous_job.company.name';
    }

    const qbCopy = QuickBuildService.copyQuickBuild(this.quickBuild);

    const categoryFilter: UIFilter = {
      filter: {
        field: breakdownBy,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [category],
      },
    };

    Log.d('job-starts-and-ends-component getContextMenuItems categoryFilter', categoryFilter);

    if (this._selectedBreakdownBy.startsWith('jobs')) {
      QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], 'moneyball');
    } else {
      QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], '__root__');
    }

    return [
      {
        label: `View ${category} employees that work at ${series.name}`,
        icon: 'group',
        action: (p) => {
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.jobsGroupType = 'active';
          qbCopy.name = `${category} employees at ${series.name}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  // todo - use option toggle component
  toggleValueType(value: string) {
    if (!value) return;

    Log.d('DemographicsCurrentComponent toggleValueType', value);
    this._selectedValueType = value;
    this.emitConfiguration();
    this.chart.update(
      this._selectedValueType === 'percentage' ? pctValuesChartOptions : numberValuesChartOptions,
      true,
      true
    );
    this.resetChartData();
    this.getChartData();
  }
}
